import { gql, type TypedDocumentNode } from '@apollo/client';

import { CloseButton } from '@sorare/core/src/atoms/buttons/CloseButton';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { SecondaryPageBackground } from '@sorare/core/src/atoms/ui/SecondaryPageBackground';
import { Dialog } from '@sorare/core/src/components/dialog';
import { SeriesBalance } from '@sorare/core/src/components/navigation/AppNavigation/UserCurrencies/SeriesBalance';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import { PlayMissions } from '#src/components/missions/PlayMissions/index.tsx';

import type {
  PlayMissionsDialogQuery,
  PlayMissionsDialogQueryVariables,
} from './__generated__/index.graphql.ts';

const PLAY_MISSIONS_DIALOG_QUERY = gql`
  query PlayMissionsDialogQuery($sport: Sport!) {
    currentUser {
      slug
      ...PlayMissions_currentUser
    }
  }
  ${PlayMissions.fragments.currentUser}
` as TypedDocumentNode<
  PlayMissionsDialogQuery,
  PlayMissionsDialogQueryVariables
>;

type Props = {
  open: boolean;
  onClose: () => void;
};

export const PlayMissionsDialog = ({ open, onClose }: Props) => {
  const { sport } = useSportContext();
  const { data, refetch } = useQuery(PLAY_MISSIONS_DIALOG_QUERY, {
    variables: { sport },
    skip: !open,
  });

  return (
    <Dialog open={open} onClose={onClose} hideHeader maxWidth="sm" fullWidth>
      <div className="relative isolate min-h-full">
        <div className="absolute inset-0 overflow-hidden">
          <SecondaryPageBackground
            overlayStartColor="var(--c-surface-light)"
            overlayMidColor="var(--c-green-400)"
            overlayEndColor="var(--c-dark-primary)"
          />
        </div>
        <Vertical gap={0} className="relative p-2">
          <SBHorizontal className="py-1">
            <SeriesBalance color="tertiary" />
            <CloseButton onClose={onClose} size="small" />
          </SBHorizontal>
          <PlayMissions
            currentUser={data?.currentUser}
            refetch={() => {
              refetch();
            }}
          />
        </Vertical>
      </div>
    </Dialog>
  );
};
